import { useLayoutEffect, useRef, useMemo, useEffect } from "react";
// eslint-disable-next-line no-undef
const { WindowVirtualizer, Virtualizer } = require("virtua");

// --- SessionStorage Availability Check ---
function isSessionStorageAvailable() {
  try {
    if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
      return false;
    }
    const testKey = "__restorable_list_test__";
    window.sessionStorage.setItem(testKey, testKey);
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn(`SessionStorage is not available or accessible: ${e.message}`);
    return false;
  }
}

const RestorableList = ({ children, useVirtualizer = false }) => {
  const cacheKey = "window-list-cache-1";
  const ref = useRef(null);

  // Check if sessionStorage is available
  const isSessionStorageAvailable = () => {
    try {
      const testKey = "__storage_test__";
      window.sessionStorage.setItem(testKey, testKey);
      window.sessionStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn("sessionStorage not available:", e);
      return false;
    }
  };

  // Safe sessionStorage getter
  const safeGetItem = (key) => {
    try {
      if (isSessionStorageAvailable()) {
        return window?.sessionStorage?.getItem(key);
      }
      return null;
    } catch (e) {
      console.warn("Error accessing sessionStorage:", e);
      return null;
    }
  };

  // Safe sessionStorage setter
  const safeSetItem = (key, value) => {
    try {
      if (isSessionStorageAvailable()) {
        window?.sessionStorage?.setItem(key, value);
      }
    } catch (e) {
      console.warn("Error setting sessionStorage:", e);
    }
  };

  // Safe sessionStorage remover
  const safeRemoveItem = (key) => {
    try {
      if (isSessionStorageAvailable()) {
        window?.sessionStorage?.removeItem(key);
      }
    } catch (e) {
      console.warn("Error removing from sessionStorage:", e);
    }
  };

  // Add error handling for ResizeObserver
  useEffect(() => {
    const handleError = (error) => {
      if (error.message.includes("ResizeObserver")) {
        error.stopPropagation();
        return false;
      }
    };
    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  const [offset, cache] = useMemo(() => {
    let serialized;

    serialized = safeGetItem(cacheKey);

    if (!serialized) return [];
    try {
      return JSON.parse(serialized);
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      return [];
    }
  }, []);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const handle = ref.current;
    window.scrollTo(0, offset ?? 0);
    safeRemoveItem(cacheKey);
    let scrollY = 0;
    const onScroll = () => {
      scrollY = window.scrollY;
    };
    window.addEventListener("beforeunload", () => {
      safeRemoveItem(cacheKey);
    });
    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      // Use stored window.scrollY because it may return 0 in useEffect cleanup
      safeSetItem(cacheKey, JSON.stringify([scrollY, handle.cache]));
    };
  }, [offset]);

  if (useVirtualizer) {
    return <Virtualizer>{children}</Virtualizer>;
  }

  return (
    <WindowVirtualizer ref={ref} cache={cache}>
      {children}
    </WindowVirtualizer>
  );
};

export default RestorableList;
